import {Component, computed, effect, inject, input, signal} from '@angular/core';
import {DecimalPipe} from '@angular/common';
import {FormGroup} from '@angular/forms';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {TranslatePipe} from '@ngx-translate/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';
import {TableModule} from 'primeng/table';
import {InvoiceService} from '../../../../../api';
import {ElciaCard} from '../../../../components/card/card';
import {Loader} from '../../../../components/loader/loader';
import {InvoiceModelForm} from '../invoice-form/invoice-form';

@Component({
  selector: 'elcia-invoice-preview',
  imports: [TranslatePipe, DecimalPipe, TableModule, ElciaCard, Loader],
  templateUrl: './invoice-preview.html',
  styleUrl: './invoice-preview.scss'
})
export class InvoicePreview {
  readonly fg = input.required<FormGroup<InvoiceModelForm>>();

  private readonly invoiceService = inject(InvoiceService);
  private readonly sanitizer = inject(DomSanitizer);

  protected readonly invoiceId = computed(() => this.fg().controls.id.value);
  protected readonly hasInvoice = computed(() => this.invoiceId() != null);

  private readonly pdfQuery = injectQuery(() => {
    const id = this.invoiceId();
    return {
      queryKey: ['invoicePdf', id],
      queryFn: () => lastValueFrom(this.invoiceService.getInvoicePdf(id!)),
      enabled: id != null,
    };
  });

  private readonly accountingQuery = injectQuery(() => {
    const id = this.invoiceId();
    return {
      queryKey: ['invoiceAccountingOperations', id],
      queryFn: () => lastValueFrom(this.invoiceService.getInvoiceAccountingOperations(id!)),
      enabled: id != null,
    };
  });

  protected readonly isPdfLoading = computed(() => this.pdfQuery.isLoading());
  protected readonly isPdfError = computed(() => this.pdfQuery.isError());

  protected readonly isAccountingLoading = computed(() => this.accountingQuery.isLoading());
  protected readonly operations = computed(() => this.accountingQuery.data()?.accountingOperations ?? []);
  protected readonly totalDebit = computed(() => this.operations().reduce((sum, op) => sum + op.debit, 0));
  protected readonly totalCredit = computed(() => this.operations().reduce((sum, op) => sum + op.credit, 0));

  protected readonly pdfUrl = signal<SafeResourceUrl | null>(null);

  private readonly bindPdfUrl = effect(onCleanup => {
    const blob = this.pdfQuery.data();
    if (!blob) {
      this.pdfUrl.set(null);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    this.pdfUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(`${objectUrl}#pagemode=none`));
    onCleanup(() => URL.revokeObjectURL(objectUrl));
  });
}
